// Shared person resolution (Epic E9 "Names, not IDs" — P1-E9-1, closes audit X-4).
//
// `usePeople()` resolves raw user ids (USR-…) to human-readable display names from the
// `/users` endpoint. The roster is fetched at most once per session via a module-level
// cache shared by every consumer (PersonLabel, PersonPicker, page-level callers), so a
// page full of person labels costs a single GET.
//
// Resolution rules (AC-UX-3):
// - a known id resolves to the user's `displayName` (with `title` available on the record)
// - an unknown id falls back to the raw id so data is never hidden or fabricated
// - the raw id stays available for details disclosure (PersonLabel puts it in `title`)

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './apiClient.js';

let peopleCache = null; // Array<{ id, displayName, title, … }> once loaded
let peopleInflight = null; // shared promise while the single fetch is running
let peopleError = null;

/** Test-only: clear the module-level roster cache between test cases. */
export function __resetPeopleCacheForTests() {
  peopleCache = null;
  peopleInflight = null;
  peopleError = null;
}

/**
 * Test-only: seed the roster cache so components resolve names synchronously
 * on first render (no fetch, no async state updates inside tests).
 */
export function __setPeopleCacheForTests(list) {
  peopleCache = Array.isArray(list) ? list : [];
  peopleInflight = null;
  peopleError = null;
}

function fetchPeopleOnce() {
  if (peopleCache) return Promise.resolve(peopleCache);
  if (!peopleInflight) {
    let request;
    try {
      // Promise.resolve guards test doubles whose `get` returns undefined.
      request = Promise.resolve(api.get('/users', { query: { limit: 500 } }));
    } catch (error) {
      request = Promise.reject(error);
    }
    peopleInflight = request
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        peopleCache = list;
        peopleError = null;
        return peopleCache;
      })
      .catch((error) => {
        // Leave the cache unset so a later mount can retry; resolvers fall back to raw ids.
        peopleError = error;
        peopleInflight = null;
        return [];
      });
  }
  return peopleInflight;
}

export function usePeople() {
  const [people, setPeople] = useState(() => peopleCache || []);
  const [loaded, setLoaded] = useState(() => Boolean(peopleCache));
  const [error, setError] = useState(() => peopleError);

  useEffect(() => {
    let alive = true;
    if (!peopleCache) {
      fetchPeopleOnce().then((list) => {
        if (!alive) return;
        setPeople(list);
        setLoaded(true);
        setError(peopleError);
      });
    }
    return () => {
      alive = false;
    };
  }, []);

  const byId = useMemo(() => new Map(people.map((person) => [person.id, person])), [people]);

  // Resolve a user id to its display name; unknown/unloaded ids fall back to the raw id.
  const nameOf = useCallback(
    (userId) => {
      if (!userId) return '';
      const person = byId.get(userId);
      return person?.displayName || String(userId);
    },
    [byId],
  );

  // Full user record for a given id (or null when unknown).
  const personOf = useCallback((userId) => (userId ? byId.get(userId) || null : null), [byId]);

  return { people, loaded, error, nameOf, personOf };
}
